import global from "../global.js";
import {Axis, AXIS_NORTH, AXIS_EAST, DIRECTION_NORTH_WEST, DIRECTION_SOUTH_WEST, DIRECTION_SOUTH_EAST, DIRECTION_NORTH_EAST} from "./Axis.js";
import Transform from "./Transform.js";
import Frame from "./Frame.js";

import RateCap from "../utils/RateCap.js";
import SubGrid from "./SubGrid.js";
import SubAxis from "./SubAxis.js";

const d3 = global.d3;

const log = global.log.getLogger("f_mtac.scene.grid");
log.setLevel(log.levels.WARN);

/**
 * @brief The class Grid is responsible for drawing the grid, the quadrants, the rubik and the axes (= coordinates)
 * @param {*} view
 * @param {*} svg
 * @param {Objects} objects instance of scene/Objects.js*/
let Grid = function (view, svg, objects) {
    var colors = global.default_configuration.grid.grid.colors;
    var color_by_value = Object.fromEntries(colors.map((color) => [color.value, color]));
    var is_extended = false;
    var ignore_axis = false; // used during grid_resize and grid_move
    let nb_squares_max; // used as upper limit of number of squares in screen during drawing of sub_grids
    global.default_configuration.grid.sub_grid.nb_squares_max ? (nb_squares_max = global.default_configuration.grid.sub_grid.nb_squares_max) : (nb_squares_max = 2); // default value of 2
    let sub_grids_list = {}; // list of sub_grids used for update on transform/translation
    let sub_axis_list = {}; // list of sub_axis used for update on transform/translation
    let existing_sub_grids = {}; // list of existing sub_grids used to not redraw/delete all sub_grids at each modification

    // cc is for "computed_configuration"
    // cc is a set of functions which computes grid values, depending on the type of grid, the reference_position, etc...
    // c is the grid.configuration
    var cc = {
        square: {
            width: (c) => {
                //log.debug("cc.grid.square.width in");
                let _w;
                if (is_extended === false) {
                    _w = Math.max(cc.grid.width(c) / c.square.relative_width, cc.grid.height(c) / c.square.relative_height);
                    //log.debug(`Grid.cc.square.width width = ${cc.grid.width(c)}, relative_width = ${c.square.relative_width}`);
                    //log.debug(`Grid.cc.square.width height = ${cc.grid.height(c)}, relative_height = ${c.square.relative_height}, width = ${_w}`);
                } else {
                    _w = Math.max(c.grid.common_size.width / c.square.relative_width, c.grid.common_size.height / c.square.relative_height);
                    //log.debug(`c.grid.common_size.width = ${c.grid.common_size.width}, relative_width = ${c.square.relative_width}`);
                    //log.debug(`c.grid.common_size.height = ${c.grid.common_size.height}, relative_height = ${c.square.relative_height}, width = ${_w}`);
                }
                return _w;
            },
            height: (c) => cc.square.width(c), // height = width  //Math.max(cc.grid.width(c) / c.square.relative_width, cc.grid.height(c) / c.square.relative_height),
            skew: (c) => cc.square.width(c) / cc.square.height(c)
        },
        axis: {
            x: {
                min: (c) => {
                    let _min;
                    if (c.frame.reference.position >= DIRECTION_SOUTH_EAST) {
                        _min = Math.max(0, Math.floor((c.resized_grid.width - (c.origin.x - c.position.x)) / cc.square.width(c)));
                    } else {
                        _min = c.position.x < 0 ? Math.ceil(Math.abs(c.position.x) / cc.square.width(c)) : 0;
                        //log.debug(`Grid.cc.axis.x.min pos.x = ${c.position.x}, square.width = ${cc.square.width(c)}`);
                    }
                    //log.debug(`Grid.axis.x.min = ${_min}`);
                    return _min;
                },
                max: (c) => {
                    let _max;
                    if (is_extended == true) {
                        // background with extended grid
                        _max = c.frame.reference.position >= DIRECTION_SOUTH_EAST ? Math.floor(c.origin.x / cc.square.height(c)) + cc.axis.x.min(c) : Math.floor(cc.grid.width(c) / cc.square.width(c));
                    } else {
                        // basic non-geolocalized picture
                        // take into account position.x so that coordinates are displayed when grid is moved to the right
                        // letters after "P" are displayed
                        _max = c.frame.reference.position >= DIRECTION_SOUTH_EAST ? Math.floor(c.origin.x / cc.square.height(c)) + cc.axis.x.min(c) : Math.floor((cc.grid.width(c) - c.position.x) / cc.square.width(c));
                    }

                    //log.debug(`Grid.axis.x.max grid.width = ${cc.grid.width(c)}`);
                    //log.debug(`Grid.axis.x.max square.width = ${cc.square.width(c)}`);
                    //log.debug(`Grid.axis.x.max = ${_max}`);
                    return _max;
                },
                range: (c) => {
                    let _range = cc.axis.x.max(c) - cc.axis.x.min(c);
                    //log.debug(`Grid.axis.x.range = ${_range}`);
                    return _range;
                }
            },
            y: {
                min: (c) => {
                    let _min;
                    if (c.frame.reference.position >= DIRECTION_SOUTH_WEST && c.frame.reference.position <= DIRECTION_SOUTH_EAST) {
                        _min = Math.max(0, Math.floor((c.resized_grid.height - (c.origin.y - c.position.y)) / cc.square.width(c)));
                    } else {
                        _min = c.position.y < 0 ? Math.ceil(Math.abs(c.position.y) / cc.square.width(c)) : 0;
                    }
                    //log.debug(`Grid.axis.y.max = ${_min}`);
                    return _min;
                },
                max: (c) => {
                    let _max;
                    if (is_extended == true) {
                        // background with extended grid
                        _max =
                            c.frame.reference.position >= DIRECTION_SOUTH_WEST && c.frame.reference.position <= DIRECTION_SOUTH_EAST
                                ? Math.floor(c.origin.y / cc.square.height(c)) + cc.axis.y.min(c)
                                : Math.floor(cc.grid.height(c) / cc.square.height(c)); // changed : Math.floor((cc.grid.height(c) - c.position.y) / cc.square.height(c));
                    } else {
                        // basic non-geolocalized picture
                        // take into account position.y so that coordinates are displayed when grid is moved to the up
                        // numbers after 16 are displayed
                        _max =
                            c.frame.reference.position >= DIRECTION_SOUTH_WEST && c.frame.reference.position <= DIRECTION_SOUTH_EAST
                                ? Math.floor(c.origin.y / cc.square.height(c)) + cc.axis.y.min(c)
                                : Math.floor((cc.grid.height(c) - c.position.y) / cc.square.height(c));
                    }
                    //log.debug(`Grid.axis.y.max = ${_max}`);
                    return _max;
                },
                range: (c) => {
                    let _range = cc.axis.y.max(c) - cc.axis.y.min(c);
                    //log.debug(`Grid.axis.y.range = ${_range}`);
                    return _range;
                }
            }
        },
        quadrant: {
            // width calculated = screen size - command_area_right width - command_area_left width  (calculated in translate_quadrants?)
            width: (c) => c.grid.size.width,
            height: (c) => c.grid.size.height
        },
        grid: {
            width: (c) => {
                //log.debug(`Grid.grid.width = ${c.grid.size.width}`);
                return c.grid.size.width;
            },
            height: (c) => c.grid.size.height,
            margin: {
                // TODO what to do with margin
                //x: (_, dc) => cc.square.width(dc) / 2.5,
                //y: (_, dc) => cc.square.height(dc) / 2.5
                x: (c) => cc.square.width(c) / 2.5,
                y: (c) => cc.square.height(c) / 2.5
            }
        },
        position: {
            // position of the grid calculated from the top left corner of the screen
            x: (c) => c.position.x,
            y: (c) => c.position.y
        },
        initial_origin: {
            // origin when background image is just created (~frame reference position). Doesn't change
            x: (c) => (c.frame.reference.position >= DIRECTION_SOUTH_EAST ? c.grid.size.width : 0),
            y: (c) => (c.frame.reference.position >= DIRECTION_SOUTH_WEST && c.frame.reference.position <= DIRECTION_SOUTH_EAST ? c.grid.size.height : 0)
        },
        origin: {
            // new background origin calculated each time the grid is moved (~ new frame reference position))
            x: (c) => (c.frame.reference.position >= DIRECTION_SOUTH_EAST ? c.grid.size.width : 0),
            y: (c) => (c.frame.reference.position >= DIRECTION_SOUTH_WEST && c.frame.reference.position <= DIRECTION_SOUTH_EAST ? c.grid.size.height : 0)
        },
        resized_grid: {
            // used to calculate axis.min instead of position sign, because when grid is resized, position sign can be opposite to original position sign
            width: (c) => c.grid.size.width,
            height: (c) => c.grid.size.height
        }
    };
    let conf = global.configuration.object_configuration("grid", cc);
    log.debug("Grid.ctor", conf);
    Object.defineProperty(this, "configuration", {
        set: () => {},
        get: () => conf
    });

    // is_extended is true for extended grid; either GIS or geo-localized picture
    Object.defineProperty(this, "is_extended", {
        set: (value) => {
            log.debug("Grid.set.is_extended", {value});
            is_extended = value;
            this.frame.is_extended = value;
            for (let dir = AXIS_NORTH; dir <= AXIS_EAST; dir++) {
                this.axis[dir].is_extended = value;
            }

            if (value === true) {
                // hide reset button when grid is extended
                global.tools.tools["grid_reset"].$button.hide();
            } else {
                // hide reset button when grid is extended
                global.tools.tools["grid_reset"].$button.show();
            }
        },
        get: () => is_extended
    });

    // when ignore_axis is true, the axis are not drawn: it improves drawing performances
    Object.defineProperty(this, "ignore_axis", {
        set: (value) => {
            ignore_axis = value;
        }
    });

    // sub_grids exist used to update frame distance text
    Object.defineProperty(this, "existing_sub_grids", {
        get: () => existing_sub_grids
    });

    // create a HTLM element with corresponding ID
    this.grid_layer = svg.layer_create("grid");
    this.sub_grid_layer = svg.layer_create("sub_grid");
    this.axis_layer = svg.layer_create("axis");
    this.sub_axis_layer = svg.layer_create("sub_axis");
    this.frame_layer = svg.layer_create("frame");
    this.quadrant_layer = svg.layer_create("quadrant");
    this.distance_layer = svg.layer_create("distance");

    this.grid = null;
    this.square = null;
    this.origin = null;

    this.frame = new Frame(view, svg, conf, this.frame_layer, this.quadrant_layer, this.distance_layer);
    this.frame.locked_rubik = false;
    this.frame.locked_first_square = false;

    this.axis = new Array(4);
    for (var d = AXIS_NORTH; d <= AXIS_EAST; d++) {
        let t = d % 2; // axis type
        this.axis[d] = new Axis(view, svg, conf, this.axis_layer, t, d);
    }
    this.axis.locked_first_square = false;

    this.grid = this.grid_layer.append("g").attr("class", "grid");
    this.grid.append("rect");

    this.grid_pattern = svg.defs.append("pattern").attr("id", "grid_pattern").attr("patternUnits", "userSpaceOnUse");
    this.grid_pattern_square = this.grid_pattern.append("rect").attr("class", "square").attr("stroke-width", "1").attr("vector-effect", "non-scaling-stroke");
    this.sub_grid_pattern = svg.defs.append("pattern").attr("id", "sub_grid_pattern").attr("patternUnits", "userSpaceOnUse");
    this.sub_grid_pattern_square = this.sub_grid_pattern.append("rect").attr("class", "square").attr("stroke-width", "1").attr("vector-effect", "non-scaling-stroke");

    svg.layer_insert("grid", "scene");
    svg.layer_insert("frame", "scene"); //Rubiks
    svg.layer_append("quadrant");
    svg.layer_append("axis");
    svg.layer_append("sub_axis");
    svg.layer_append("distance");

    /**
     * @brief Do some initialisations for axis
     */
    this.init_axis = function () {
        log.debug("Grid.init_axis in");
        for (let dir = AXIS_NORTH; dir <= AXIS_EAST; dir++) {
            this.axis[dir].init_data();
        }
    };
    /**
     * Hide the grid layers
     */
    this.undraw = function () {
        this.transform.disable();
        svg.layer_hide("grid");
        svg.layer_hide("sub_grid");
        svg.layer_hide("axis");
        svg.layer_hide("sub_axis");
        svg.layer_hide("frame");
        svg.layer_hide("quadrant");
        svg.layer_hide("distance");
    };

    /**
     * Draw the elements of the grid: the grid itselg, the quadrants, the axes, the rubik
     */
    const draw = () => {
        log.debug("Grid.draw IN", {is_extended, ignore_axis});
        let OX = conf.get("origin.x");
        if (OX === undefined) {
            // not ready to draw the grid
            return;
        }
        let OY = conf.get("origin.y");

        svg.layer_show("grid");
        if (is_extended === true) {
            svg.layer_show("sub_grid");
        }
        if (ignore_axis === false) {
            svg.layer_show("axis");
            if (is_extended === true) {
                svg.layer_show("sub_axis");
            }
        }
        svg.layer_show("frame");
        svg.layer_show("quadrant");
        svg.layer_show("distance");
        const W = conf.get("square.width"),
            H = conf.get("square.height");
        //log.debug("Grid.draw W,H", {W,H});

        const GC = conf.get("grid.color"),
            GO = conf.get("grid.opacity");
        const GV = conf.get("visibility");
        let GW = conf.get("grid.size.width");
        let GH = conf.get("grid.size.height");
        //log.debug("Grid.draw GW,GH", {GW,GH});

        let SW = parseFloat($("#grid_pattern .square").attr("stroke-width"));

        if (is_extended == false) {
            // If it is a basic grid, the grid display is limited inside the picture
            // so, if the grid has been moved, the size has to be reduced by the move value
            // so that the grid doesn't overflow the picture
            switch (conf.get("frame.reference.position")) {
                case DIRECTION_NORTH_WEST: // standard
                    //log.debug("Grid.draw DIRECTION_NORTH_WEST");
                    if (conf.get("position.x") > 0) {
                        GW = conf.get("grid.size.width") - OX;
                    }
                    if (conf.get("position.y") > 0) {
                        GH = conf.get("grid.size.height") - OY;
                    }
                    break;
                case DIRECTION_SOUTH_WEST: // GIGN
                    //log.debug("Grid.draw DIRECTION_SOUTH_WEST");
                    if (conf.get("position.x") > 0) {
                        GW = conf.get("grid.size.width") - OX;
                    }
                    if (conf.get("axis.y.min") === 0) {
                        GH = OY + SW;
                    }
                    break;
                /*case DIRECTION_SOUTH_EAST:
                    //log.debug("Grid.draw DIRECTION_SOUTH_EAST");
                    if (conf.get("axis.x.min") === 0) {
                        GW = OX + SW;
                    }
                    if (conf.get("axis.y.min") === 0) {
                        GH = OY + SW;
                    }
                    break;
                case DIRECTION_NORTH_EAST:
                    //log.debug("Grid.draw DIRECTION_NORTH_EAST");
                    if (conf.get("axis.x.min") === 0) {
                        GW = OX + SW;
                    }
                    if (conf.get("position.y") > 0) {
                        GH =  conf.get("size.height") - OY;
                    }
                    break;*/
            }
        }

        ///////////////////////////////////////////////////
        // drawing grid pattern
        ///////////////////////////////////////////////////

        // apply translation for the intersection to match the top_left lon/lat coordinate
        let _x =
            conf.get("frame.reference.position") >= DIRECTION_SOUTH_EAST
                ? conf.get("position.x") + (((OX - conf.get("position.x")) / W) % 1) * W //GIGN
                : conf.get("position.x"); // standard

        let _y = conf.get("frame.reference.position") >= DIRECTION_SOUTH_WEST && conf.get("frame.reference.position") <= DIRECTION_SOUTH_EAST ? conf.get("position.y") + (((OY - conf.get("position.y")) / W) % 1) * W : conf.get("position.y");

        //log.debug( "Grid.draw pattern for extended grid", {_x, _y, W, H});
        this.grid_pattern.attr("x", _x).attr("y", _y).attr("width", W).attr("height", H);

        ///////////////////////////////////////////////////
        // drawing square pattern
        ///////////////////////////////////////////////////

        // we draw a square in each grid pattern
        this.grid_pattern_square
            //.attr("x", 0) useless the square is display inside the pattern
            //.attr("y", 0)
            .attr("width", W) // square width
            .attr("height", H) // square height
            .style("fill", "none")
            .style("stroke", GC)
            .style("opacity", GO);
        this.grid = this.grid_layer.select("g.grid");
        if (this.grid.size() === 0) {
            this.grid = this.grid_layer.append("g").attr("class", "grid");
        }

        ///////////////////////////////////////////////////
        // drawing rect = area where the grid is diplayed
        ///////////////////////////////////////////////////

        // if none-geolocalized picture
        if (is_extended === false) {
            let grid_x, grid_y;
            if (conf.get("frame.reference.position") >= DIRECTION_SOUTH_EAST) {
                grid_x = 0;
            } else {
                grid_x = conf.get("position.x") <= 0 ? 0 : OX;
            }
            if (conf.get("frame.reference.position") >= DIRECTION_SOUTH_WEST && conf.get("frame.reference.position") <= DIRECTION_SOUTH_EAST) {
                grid_y = 0;
            } else {
                grid_y = conf.get("position.y") < 0 ? 0 : OY;
            }
            let _width = Math.min(GW, conf.get("grid.size.width"));
            let _height = Math.min(GH, conf.get("grid.size.height"));

            //log.debug("Grid.draw classical rect ", {grid_x, grid_y, _width, _height});
            this.grid.select("rect").attr("x", grid_x).attr("y", grid_y).attr("width", _width).attr("height", _height).attr("fill", "url(#grid_pattern)");
        }
        // else geolocalized --> extended grid
        else {
            let _x = -this.transform.x / this.transform.k;
            let _y = -this.transform.y / this.transform.k;
            let _w = window.innerWidth / this.transform.k;
            let _h = window.innerHeight / this.transform.k;
            //log.debug("Grid.draw extended rect", {_x, _y, _w, _h});
            // display the grid on all the screen
            this.grid
                .select("rect")
                .attr("x", _x) // draw from left of the screen
                .attr("y", _y) // draw from top of the screen
                .attr("width", _w) // draw full screen size
                .attr("height", _h) // draw full screen size
                .attr("fill", "url(#grid_pattern)");
        }

        ///////////////////////////////////////////////////
        // drawing axis
        ///////////////////////////////////////////////////
        if (ignore_axis === false) {
            for (let d = AXIS_NORTH; d <= AXIS_EAST; d++) {
                this.axis[d].draw();
            }
        }

        ///////////////////////////////////////////////////
        // drawing the rubik
        ///////////////////////////////////////////////////
        this.frame.draw();

        ///////////////////////////////////////////////////
        // drawing the sub_grid/sub_axis
        ///////////////////////////////////////////////////
        if (is_extended == true) {
            compute_sub_grids();
        }

        Object.entries(sub_grids_list).forEach(([key, value]) => {
            log.debug(value);
            if (is_extended === true) {
                sub_grids_list[key].draw();
            }
        });

        Object.entries(sub_axis_list).forEach(([key, value]) => {
            log.debug(value);
            if (is_extended === true) {
                sub_axis_list[key].forEach((element) => {
                    element.init();
                });
            }
        });

        if (GV) {
            this.show();
        } else {
            this.hide();
        }

        this.transform.enable();

        window.document.documentElement.style.setProperty("--grid-color", GC);
        window.document.documentElement.style.setProperty("--grid-color-inverse", color_by_value[GC].inverse);
    };

    // RateCap handles a mechanism that delaies the drawing few milliseconds later
    // With RateCap, several quick calls to draw() may be result in one draw
    const draw_cap = new RateCap(() => {
        draw();
    }, 50);
    this.draw = () => {
        draw_cap.poll();
    };

    /**
     * @brief Hide the grid layers
     */
    this.hide = () => {
        svg.layer_hide("grid");
        svg.layer_hide("sub_grid");
        svg.layer_hide("frame");
        svg.layer_hide("quadrant");
        svg.layer_hide("distance");
        if (ignore_axis === false) {
            svg.layer_hide("axis");
            svg.layer_hide("sub_axis");
        }
    };

    /**
     * @brief Show the grid layers
     */
    this.show = () => {
        svg.layer_show("grid");
        if (is_extended === true) {
            svg.layer_show("sub_grid");
        }
        svg.layer_show("frame");
        svg.layer_show("quadrant");
        svg.layer_show("distance");
        if (ignore_axis === false) {
            svg.layer_show("axis");
            if (is_extended === true) {
                svg.layer_show("sub_axis");
            }
        }
    };

    /**
     * @brief Apply the transformations (x,y translation, and k scale) to the grid (only the grid, not the axes the quadrants, etc...)
     */
    this.transform = new Transform({x: 0, y: 0, k: 1});
    this.transform.on_change(({x, y, k}) => {
        this.grid.attr("transform", "translate(" + x + "," + y + ") scale(" + k + ")");
        if (is_extended === true) {
            // display the grid on all the screen
            this.grid
                .select("rect")
                .attr("x", -x / k) // balance the x translate so that grid is always displayed from the left
                .attr("y", -y / k) // balance the y translate so that grid is always displayed from the top
                .attr("width", window.innerWidth / k)
                .attr("height", window.innerHeight / k);
            compute_sub_grids();
        }
    });

    /**
     * @brief When scale is changed (zoom or unzoom), modify the grid lines width
     * @param k input, the scale factor
     */
    this.transform.on_scale(({k}) => {
        //log.debug("Grid.transform.on_scale", {k});
        if (is_extended === false) {
            this.grid_pattern_square.attr("stroke-width", `${Math.exp(-k / 10)}`);
        } else {
            // size of the square in pixels
            let size = conf.get("square.width");

            // we want the stroke-width to be proportionate to the square size, with a ratio of 0.03
            // width_displayed is the displayed width (nb of pixels) with the given scale
            let width_displayed = size * 0.03 * k;

            // width is the value to be passed to stroke-width
            let width = size * 0.03;
            //log.debug("Grid.transform.on_scale", {k, size, width_displayed});

            // max width is 3.0
            if (width_displayed > 3.0) {
                width = 3.0 / k;
            }
            this.grid_pattern_square.attr("stroke-width", width);
            this.sub_grid_pattern_square.attr("stroke-width", 1.0 / k);
        }
    });

    for (let d = AXIS_NORTH; d <= AXIS_EAST; d++) {
        this.transform.append_child(this.axis[d].transform);
    }
    this.transform.append_child(this.frame.transform);
    this.transform.disable();

    /**
     * @brief Computes the relative size, given the grid size (in pixel) and the square size (in pixel).
     * @Note The "relative size" is the number of squares in the longest size
     * @Note square.relative_width and square.relative_height have always the same value
     * @param {float} square_side in, number of pixels of the square side
     * @returns
     */
    this.square_size_update = (square_side) => {
        log.debug("Grid.square_size_update in", square_side);
        let GW, GH;

        // the relative size is relative to:
        // . the picture size if it is a picture with local grid
        // . the common_size for the extended grid
        if (is_extended === false) {
            GW = conf.get("grid.width"); // total width in pixel
            GH = conf.get("grid.height"); // toal height in pixel
        } else {
            GW = conf.get("grid.common_size.width"); // total width in pixel
            GH = conf.get("grid.common_size.height"); // toal height in pixel
        }

        const SRW = GW / square_side;
        const SRH = GH / square_side;
        log.debug("Grid.square_size_update", {GW, GH, SRW, SRH});
        let new_relative_width = Math.max(SRW, SRH);

        // the number os squares in the size is limited to the range [1,64]
        if (new_relative_width < 1) {
            return false;
        }
        if (new_relative_width > 64) {
            return false;
        }

        log.debug(`Grid.square_size_update square.relative_width,height = ${new_relative_width}, ${new_relative_width}`);
        conf.set("square.relative_width", new_relative_width);
        conf.set("square.relative_height", new_relative_width);

        return true;
    };

    /**
     * @brief Initiate the place of the origin (for the rubik) depending on the reference.position
     */
    this.set_picture_grid_origin = async () => {
        if (is_extended === false) {
            log.debug(`Grid.set_picture_grid_origin in origin.x = ${conf.get("origin.x")}, origin.y = ${conf.get("origin.y")}`);
            switch (conf.get("frame.reference.position")) {
                case DIRECTION_NORTH_WEST: // standard
                    conf.set("origin.x", conf.get("position.x"));
                    conf.set("origin.y", conf.get("position.y"));
                    break;
                case DIRECTION_SOUTH_WEST: // GIGN
                    conf.set("origin.y", conf.get("grid.size.height"));
                    conf.set("resized_grid.height", conf.get("grid.size.height"));
                    break;
                case DIRECTION_SOUTH_EAST:
                    conf.set("origin.x", conf.get("grid.size.width"));
                    conf.set("origin.y", conf.get("grid.size.height"));
                    conf.set("resized_grid.width", conf.get("grid.size.width"));
                    conf.set("resized_grid.height", conf.get("grid.size.height"));
                    break;
                case DIRECTION_NORTH_EAST:
                    conf.set("origin.x", conf.get("grid.size.width"));
                    conf.set("resized_grid.width", conf.get("grid.size.width"));
                    break;
            }
            log.debug(`Grid.set_picture_grid_origin out origin.x = ${conf.get("origin.x")}, origin.y = ${conf.get("origin.y")}`);
        }
    };

    /**
     * @brief get the grid data attributes in Objects,
     * @returns the sub-Object referencing the dataGrid
     */
    this.get_grid_data_object = () => {
        // the background image is the first object of layer "media"
        let local_id = objects.layers.media.ids[0];
        if (local_id !== undefined) {
            let background = objects.layers.media.objects[local_id];
            if (background.attributes.dataSubtype == "background_image") {
                return background.attributes.dataGrid;
            }
        }
        log.warn("Grid.get_grid_data_object Not found");
        return undefined;
    };

    /**
     * @brief get the common grid data attributes in Objects,
     * @returns the sub-Object referencing the dataCommonGrid
     */
    this.get_common_grid_data_object = () => {
        // the background image is the first object of layer "media"
        let local_id = objects.layers.media.ids[0];
        if (local_id !== undefined) {
            let background = objects.layers.media.objects[local_id];
            if (background.attributes.dataSubtype == "background_image") {
                return background.attributes.dataCommonGrid;
            }
        }
        log.warn("Grid.get_common_grid_data_object Not found");
        return undefined;
    };

    /**
     * @brief get the background attributes in Objects,
     * @returns the sub-Object referencing the attributes
     */
    this.get_background_attributes_object = () => {
        // the background image is the first object of layer "media"
        let local_id = objects.layers.media.ids[0];
        if (local_id !== undefined) {
            let background = objects.layers.media.objects[local_id];
            if (background.attributes.dataSubtype == "background_image") {
                return background.attributes;
            }
        }
        log.warn("Grid.get_grid_data_object Not found");
        return undefined;
    };

    /**
     * @brief Compute the attributes of the common grid (top_left, top_right, ...) from the image projection
     * and save in the Objects after resize
     */
    this.init_common_grid_from_image = async () => {
        log.debug("Grid.init_common_grid_from_image in");
        let data_grid = this.get_grid_data_object();

        // check if there is a common grid and a projection
        if (data_grid !== undefined && global.storage.selected_context.projection.project != null) {
            log.debug("Grid.init_common_grid_from_image set common grid", data_grid);
            let data = JSON.parse(JSON.stringify(data_grid));

            // compute common grid data
            let common_grid = {};
            common_grid.top_left = global.storage.selected_context.projection.project_invert([data.position.x, data.position.y]);
            common_grid.top_right = global.storage.selected_context.projection.project_invert([data.position.x + data.grid.common_size.width, data.position.y]);
            common_grid.bottom_left = global.storage.selected_context.projection.project_invert([data.position.x, data.position.y + data.grid.common_size.height]);
            common_grid.square_relative_width = data.square.relative_width;
            common_grid.square_relative_height = data.square.relative_height;
            let json_conf = conf.default();
            common_grid.reference_position = json_conf.frame.reference.position;
            common_grid.reference_colors = json_conf.frame.color;

            // compute distance
            if (data.grid.common_size.width > data_grid.grid.common_size.height) {
                common_grid.distance = d3.geoDistanceMeters(common_grid.top_left, common_grid.top_right) / data.square.relative_width;
            } else {
                common_grid.distance = d3.geoDistanceMeters(common_grid.top_left, common_grid.bottom_left) / data.square.relative_height;
            }

            // save in background Objects
            // save the common grid information as a string
            let attributes = this.get_background_attributes_object();
            attributes.dataCommonGrid = JSON.stringify(common_grid);
            log.debug("Grid.init_common_grid_from_image out", common_grid);
        }
    };

    /**
     * @brief Compute the attributes of the common grid (top_left, top_right, ...) from the image projection
     * and save in the Objects
     */
    this.update_common_grid_from_image_after_resize = async () => {
        log.debug("Grid.update_common_grid_from_image_after_resize in");
        let data_grid = this.get_grid_data_object();

        // check if there is a common grid and a projection
        if (data_grid !== undefined && global.storage.selected_context.projection.project != null) {
            log.debug("Grid.update_common_grid_from_image_after_resize set common grid", data_grid);
            let data = JSON.parse(JSON.stringify(data_grid));

            // compute common grid data
            let attributes = this.get_background_attributes_object();
            let common_grid = JSON.parse(attributes.dataCommonGrid);
            common_grid.square_relative_width = data.square.relative_width;
            common_grid.square_relative_height = data.square.relative_height;

            // compute distance
            if (data.grid.common_size.width > data_grid.grid.common_size.height) {
                common_grid.distance = d3.geoDistanceMeters(common_grid.top_left, common_grid.top_right) / data.square.relative_width;
            } else {
                common_grid.distance = d3.geoDistanceMeters(common_grid.top_left, common_grid.bottom_left) / data.square.relative_height;
            }

            // save in background Objects
            // save the common grid information as a string
            attributes.dataCommonGrid = JSON.stringify(common_grid);
            log.debug("Grid.update_common_grid_from_image_after_resize out", common_grid);
        }
    };

    /**
     * @brief Init common grid attributes from the current GIS view
     * @param {Object} dataGrid in, attributes of the grid
     * @returns the string with common grid attributes
     */
    this.init_common_grid_from_gis = (dataGrid) => {
        log.debug("Grid.init_common_grid_from_gis in");

        // not found, this is the first desktop, create the common attributes
        let ol_x, ol_y, ol_width, ol_height;

        // top left of the common grid will be the top left of this screen i.e. [0,0]
        [ol_x, ol_y] = global.view.ol_driver.from_pixel_to_ol([0, 0]);
        [ol_width, ol_height] = global.view.ol_driver.getOLSize();

        // save the common information
        let common_grid = {};
        common_grid.top_left = global.view.ol_driver.to_lon_lat([ol_x, ol_y]);
        common_grid.top_right = global.view.ol_driver.to_lon_lat([ol_x + ol_width, ol_y]);
        common_grid.bottom_left = global.view.ol_driver.to_lon_lat([ol_x, ol_y - ol_height]);
        common_grid.square_relative_width = dataGrid.square.relative_width;
        common_grid.square_relative_height = dataGrid.square.relative_height;
        let json_conf = conf.default();
        common_grid.reference_position = json_conf.frame.reference.position;
        common_grid.reference_colors = json_conf.frame.color;

        // compute distance
        if (ol_width > ol_height) {
            let distance_width = global.view.ol_driver.get_distance(common_grid.top_left, common_grid.top_right);
            common_grid.distance = distance_width / dataGrid.square.relative_width;
        } else {
            let distance_height = global.view.ol_driver.get_distance(common_grid.top_left, common_grid.top_right);
            common_grid.distance = distance_height / dataGrid.square.relative_height;
        }

        log.debug("Grid.init_common_grid_from_gis common_grid = ", common_grid);

        return JSON.stringify(common_grid);
    };

    /**
     * @brief Re-Init the attributes of the grid to the default values
     * Init the common grid to undefined. This is for pictures only
     */
    this.reset_coordinates = async () => {
        log.debug("Grid.reset_coordinates in");
        let data_grid = this.get_grid_data_object(); // data_grid is an Object structure
        if (data_grid !== undefined) {
            log.debug("Grid.reset_coordinates", data_grid);

            // get default configuration (from commissioning)
            let default_grid = this.configuration.default(); // default_grid is a Json structure

            data_grid.position.x = default_grid.position.x;
            data_grid.position.y = default_grid.position.y;
            data_grid.square.relative_width = default_grid.square.relative_width;
            data_grid.square.relative_height = default_grid.square.relative_height;

            switch (conf.get("frame.reference.position")) {
                case DIRECTION_NORTH_WEST: // standard
                    data_grid.origin.x = default_grid.origin.x;
                    data_grid.origin.y = default_grid.origin.y;
                    data_grid.resized_grid.width = global.scene.map.width;
                    data_grid.resized_grid.height = global.scene.map.height;
                    break;
                case DIRECTION_SOUTH_WEST: // GIGN
                    data_grid.origin.x = default_grid.origin.x;
                    data_grid.origin.y = global.scene.map.height;
                    data_grid.resized_grid.width = global.scene.map.width;
                    data_grid.resized_grid.height = global.scene.map.height;
                    break;
                case DIRECTION_SOUTH_EAST:
                    data_grid.origin.x = global.scene.map.width;
                    data_grid.origin.y = global.scene.map.height;
                    data_grid.resized_grid.width = global.scene.map.width;
                    data_grid.resized_grid.height = global.scene.map.height;
                    break;
                case DIRECTION_NORTH_EAST:
                    data_grid.origin.x = global.scene.map.width;
                    data_grid.origin.y = default_grid.origin.y;
                    data_grid.resized_grid.width = global.scene.map.width;
                    data_grid.resized_grid.height = global.scene.map.height;
                    break;
            }
        }

        let attributes = this.get_background_attributes_object();
        if (attributes !== undefined) {
            attributes.dataCommonGrid = undefined;
        }
    };

    /**
     * @brief Compute the common grid parameters and store them in Objects
     * After move, the position.x,y do change, the grid size does not change
     */
    this.update_common_grid_from_gis_after_move = async () => {
        log.debug("Grid.update_common_grid_from_gis_after_move in");
        let data_grid = this.get_grid_data_object();
        if (data_grid !== undefined) {
            //log.debug(`Grid.update_common_grid_from_gis_after_move data_grid = ${JSON.stringify(data_grid)}`);
            //let common = object.attributes.dataGrid.common;
            let data = JSON.parse(JSON.stringify(data_grid));

            let attributes = this.get_background_attributes_object();
            // save the common information
            let previous_common_grid = JSON.parse(attributes.dataCommonGrid);

            // -> update the common grid attributes
            let ol_x, ol_y, ol_width, ol_height;

            // top left of the common grid will be the top left position of the grid in the screen
            [ol_x, ol_y] = global.view.ol_driver.from_pixel_to_ol([data.position.x, data.position.y]);

            // compute width and height in OpenLayer coordinates, with previous common values (width and height do not change)
            let top_left_ol = global.view.ol_driver.from_lon_lat_to_ol(previous_common_grid.top_left);
            let top_right_ol = global.view.ol_driver.from_lon_lat_to_ol(previous_common_grid.top_right);
            let bottom_left_ol = global.view.ol_driver.from_lon_lat_to_ol(previous_common_grid.bottom_left);
            ol_width = top_right_ol[0] - top_left_ol[0];
            ol_height = top_left_ol[1] - bottom_left_ol[1];

            // use projection to compute lon,lat
            let common_grid = {};
            common_grid.top_left = global.view.ol_driver.to_lon_lat([ol_x, ol_y]);
            common_grid.top_right = global.view.ol_driver.to_lon_lat([ol_x + ol_width, ol_y]);
            common_grid.bottom_left = global.view.ol_driver.to_lon_lat([ol_x, ol_y - ol_height]);
            common_grid.square_relative_width = data.square.relative_width;
            common_grid.square_relative_height = data.square.relative_height;

            common_grid.distance = previous_common_grid.distance;

            // compute new distance
            log.debug("Grid.update_common_grid_from_gis_after_move: grid = ", {ol_width, ol_height});
            log.debug("Grid.update_common_grid_from_gis_after_move: square = ", {relative_width: data_grid.square.relative_width, relative_height: data_grid.square.relative_height});

            // update object and trigger drawing
            attributes.dataCommonGrid = JSON.stringify(common_grid);

            log.debug("Grid.update_common_grid_from_gis_after_move out", common_grid);
        }
    };

    /**
     * @brief Compute the common grid parameters and store them in Objects
     * After resize, the position.x,y does not change, the grid size do change
     */
    this.update_common_grid_from_gis_after_resize = async () => {
        log.debug("Grid.update_common_grid_from_gis_after_resize in");

        let data_grid = this.get_grid_data_object();
        if (data_grid !== undefined) {
            //log.debug(`Grid.update_common_grid_from_gis_after_resize data_grid = ${JSON.stringify(data_grid)}`);
            //log.debug(`Grid.update_common_grid_from_gis_after_resize position.x,y = (${data_grid.position.x},${data_grid.position.y}) origin.x,y = (${data_grid.origin.x},${data_grid.origin.x})`);
            //log.debug(`Grid.update_common_grid_from_gis_after_resize common_size.w,h = (${data_grid.grid.common_size.width},${data_grid.grid.common_size.height})`);
            let data = JSON.parse(JSON.stringify(data_grid));

            let attributes = this.get_background_attributes_object();
            // save the common information
            let common_grid = JSON.parse(attributes.dataCommonGrid);

            common_grid.square_relative_width = data.square.relative_width;
            common_grid.square_relative_height = data.square.relative_height;

            // compute new distance
            common_grid.distance = this.compute_distance_for_gis();
            log.debug("Grid.update_common_grid_from_gis_after_resize .common = ", common_grid);

            // Store and trigger drawing
            attributes.dataCommonGrid = JSON.stringify(common_grid);
            log.debug("Grid.update_common_grid_from_gis_after_resize out", common_grid);
        }
    };

    /**
     * @brief According to the GIS projection and according to the common grid information,
     *        set the grid parameters of the GIS (position, origin, size, squares)
     * @param {object} data_grid in/out, media.dataGrid object
     * @return true if done
     */
    this.init_gis_from_common_grid = async (data_grid, data_common_grid) => {
        log.debug("Grid.init_gis_from_common_grid", data_grid);
        let modified = false;

        // if data_grid not provided, get it from Objects
        if (data_grid === undefined) {
            data_grid = this.get_grid_data_object();
        }

        let common_grid;
        if (data_common_grid === undefined) {
            // get common grid data
            let attributes = this.get_background_attributes_object();
            if (attributes.dataCommonGrid === undefined) {
                return;
            }
            common_grid = JSON.parse(attributes.dataCommonGrid);
        } else {
            common_grid = JSON.parse(data_common_grid);
        }

        let position = global.view.ol_driver.from_lon_lat_position_to_pixel(common_grid.top_left);
        if (data_grid.position.x != position[0]) {
            data_grid.position.x = position[0];
            modified = true;
        }
        if (data_grid.position.y != position[1]) {
            data_grid.position.y = position[1];
            modified = true;
        }

        // compute the common grid size in the current coordinate system
        // so as to be able to get the square width (= common_size.width / square_relative_width)
        let top_left_ol = global.view.ol_driver.from_lon_lat_to_ol(common_grid.top_left);
        let top_right_ol = global.view.ol_driver.from_lon_lat_to_ol(common_grid.top_right);
        let bottom_left_ol = global.view.ol_driver.from_lon_lat_to_ol(common_grid.bottom_left);
        // width in OpenLayer
        let width_ol = top_right_ol[0] - top_left_ol[0];
        // height in OpenLayer
        let height_ol = top_left_ol[1] - bottom_left_ol[1];
        // convert to [width,height] in pixels
        let size = global.view.ol_driver.from_ol_size_to_pixel([width_ol, height_ol]);

        if (data_grid.grid.common_size.width != size[0]) {
            data_grid.grid.common_size.width = size[0];
            modified = true;
        }
        if (data_grid.grid.common_size.height != size[1]) {
            data_grid.grid.common_size.height = size[1];
            modified = true;
        }

        this.init_origin(data_grid);

        if (data_grid.square.relative_width != common_grid.square_relative_width) {
            data_grid.square.relative_width = common_grid.square_relative_width;
            modified = true;
        }

        if (data_grid.square.relative_height != common_grid.square_relative_height) {
            data_grid.square.relative_height = common_grid.square_relative_height;
            modified = true;
        }

        if (modified === true) {
            global.scene.map.grid_save();
        }

        log.debug("Grid.init_gis_from_common_grid out", data_grid);
    };

    /**
     * @brief According to the projection (provided by geonodes) and according to the common grid information
     *        found in another desktop, set the grid parameters of the media (position, origin, size, squares)
     * @param {object} data_grid in/out, media.dataGrid object
     * @return true if done
     */
    this.init_image_from_common_grid = async (data_grid) => {
        log.debug("Grid.init_image_from_common_grid in", data_grid);

        if (data_grid === undefined) {
            data_grid = this.get_grid_data_object();
        }
        let attributes = this.get_background_attributes_object();

        // check if projection exists
        log.debug("projection ", global.storage.selected_context.projection.project);
        if (global.storage.selected_context.projection.project === null) {
            log.debug("Grid.init_image_from_common_grid projection not defined yet");
            return false;
        }

        let str_common_grid = "";
        // find common grid data in current desktop
        if (attributes.dataCommonGrid !== undefined) {
            // current desktop has common grid data, get it
            str_common_grid = attributes.dataCommonGrid;
        } else {
            // check if common grid exists in an other desktop
            str_common_grid = await global.storage.selected_space.get_grid_common_data();
            if (str_common_grid === undefined) {
                log.debug("Grid.init_image_from_common_grid: common grid not found");
                return false;
            }

            // add common grid to current background object
            attributes.dataCommonGrid = str_common_grid;
        }

        log.debug("Grid.init_image_from_common_grid: str_common_grid = ", {str_common_grid});

        // Compute grid data from common grid and projection

        let common_data = JSON.parse(str_common_grid);
        log.debug("Grid.init_image_from_common_grid: json common data = ", common_data);
        data_grid.square.relative_width = common_data.square_relative_width;
        data_grid.square.relative_height = common_data.square_relative_height;
        //log.debug("Grid.init_image_from_common_grid data_grid: ", data_grid);

        // media is geo-referenced -> use extended grid
        this.is_extended = true;

        // get pixel [x,y] from [lon,lat]
        let top_left_xy = global.storage.selected_context.projection.project(common_data.top_left);
        let top_right_xy = global.storage.selected_context.projection.project(common_data.top_right);
        let bottom_left_xy = global.storage.selected_context.projection.project(common_data.bottom_left);
        log.debug("Grid.init_image_from_common_grid lonlat = ", {top_left_xy, top_right_xy, bottom_left_xy});

        // set grid attributes
        data_grid.grid.common_size = {width: top_right_xy[0] - top_left_xy[0], height: bottom_left_xy[1] - top_left_xy[1]};
        log.debug("Grid.init_image_from_common_grid common_size", data_grid.grid.common_size);
        data_grid.position.x = top_left_xy[0];
        data_grid.position.y = top_left_xy[1];

        this.init_origin(data_grid);

        log.debug("Grid.init_image_from_common_grid grid", data_grid);
        return true;
    };

    /**
     * @brief Get the common grid parameters to update the image grid
     */
    this.update_image_from_common_grid = async () => {
        let data_grid = this.get_grid_data_object();
        log.debug("Grid.update_image_from_common_grid", data_grid);

        // check projection exists
        if (global.storage.selected_context.projection.project === null) {
            log.debug("Grid.update_image_from_common_grid projection not defined yet");
            return;
        }

        // check common grid data is present
        let attributes = this.get_background_attributes_object();
        if (attributes.dataCommonGrid === undefined) {
            log.debug("Grid.update_image_from_common_grid no common");
            return;
        }

        let common_data = JSON.parse(attributes.dataCommonGrid);

        if (common_data.square_relative_width === undefined || common_data.square_relative_height === undefined || common_data.top_left === undefined || common_data.top_right === undefined || common_data.bottom_left === undefined) {
            log.debug("Grid.update_image_from_common_grid missing parameter", common_data);
            return;
        }

        log.debug("Grid.update_image_from_common_grid", common_data);

        data_grid.square.relative_width = common_data.square_relative_width;
        data_grid.square.relative_height = common_data.square_relative_height;

        // media is geo-referenced -> use extended grid
        this.is_extended = true;

        // get pixel [x,y] from [lon,lat]
        let top_left = global.storage.selected_context.projection.project(common_data.top_left);
        let top_right = global.storage.selected_context.projection.project(common_data.top_right);
        let bottom_left = global.storage.selected_context.projection.project(common_data.bottom_left);

        // set grid attributes
        data_grid.grid.common_size = {width: top_right[0] - top_left[0], height: bottom_left[1] - top_left[1]};
        log.debug("Grid.update_image_from_common_grid common_size", data_grid.grid.common_size);
        data_grid.position.x = top_left[0]; // position of [A1]
        data_grid.position.y = top_left[1];

        this.init_origin(data_grid);

        log.debug("Grid.update_image_from_common_grid grid", data_grid);
    };

    /**
     * @brief Set the position in pixel of the origin (=rubik) being given:
     *     the top-left position of the grid in pixel
     *     the with and height of the grid in pixel
     * @param {Object} data_grid in/out, assumes that position.x,y and common_size.width,height are OK
     */
    this.init_origin = (data_grid) => {
        //log.debug(`Grid.init_origin in position.x,y = {${data_grid.position.x},${data_grid.position.y}} common_size.w,h = {${data_grid.grid.common_size.width},${data_grid.grid.common_size.height}}`);
        let reference_position = conf.get("frame.reference.position");
        if (reference_position === undefined) {
            log.error("Grid.init_origin reference.position is undefined");
            return;
        }
        switch (reference_position) {
            case DIRECTION_NORTH_WEST: // standard
                data_grid.origin.x = data_grid.position.x;
                data_grid.origin.y = data_grid.position.y;
                break;
            case DIRECTION_SOUTH_WEST: // GIGN
                data_grid.origin.x = data_grid.position.x;
                data_grid.origin.y = data_grid.position.y + data_grid.grid.common_size.height;
                break;
            case DIRECTION_SOUTH_EAST:
                data_grid.origin.x = data_grid.position.x + data_grid.grid.common_size.width;
                data_grid.origin.y = data_grid.position.y + data_grid.grid.common_size.height;
                break;
            case DIRECTION_NORTH_EAST:
                data_grid.origin.x = data_grid.position.x + data_grid.grid.common_size.width;
                data_grid.origin.y = data_grid.position.y;
                break;
        }
        //log.debug(`Grid.init_origin out origin.x,y = (${data_grid.origin.x},${data_grid.origin.y})`);
    };

    /**
     * Compute the distance of the grid. The distance is the size of a square (of the grid)
     * Calculation: distance = size of screen in meters / number of squares (= square.relative_xxx)
     * @returns the distance or 0.0 if not computed
     */
    this.compute_distance_for_gis = () => {
        log.debug("Grid.compute_distance_for_gis");
        let distance = 0.0;
        let data_grid = this.get_grid_data_object();
        let attributes = this.get_background_attributes_object();
        if (attributes.dataCommonGrid !== undefined) {
            // dataCommonGrid is a string, transform to JSON
            let common = JSON.parse(attributes.dataCommonGrid);
            // -> update the common grid attributes
            let ol_width, ol_height;

            // compute common grid width and height in OpenLayers coord, with previous common values (common width and height did not change)
            let top_left_ol = global.view.ol_driver.from_lon_lat_to_ol(common.top_left);
            let top_right_ol = global.view.ol_driver.from_lon_lat_to_ol(common.top_right);
            let bottom_left_ol = global.view.ol_driver.from_lon_lat_to_ol(common.bottom_left);
            ol_width = top_right_ol[0] - top_left_ol[0];
            ol_height = top_left_ol[1] - bottom_left_ol[1];
            //log.debug("Grid.compute_distance_for_gis: grid = ", {ol_width, ol_height});
            //log.debug("Grid.compute_distance_for_gis: square = ", {relative_width:data_grid.square.relative_width, relative_height:data_grid.square.relative_height});

            // compute new distance
            if (ol_width > ol_height) {
                let distance_width = global.view.ol_driver.get_distance(common.top_left, common.top_right);
                distance = distance_width / data_grid.square.relative_width;
            } else {
                let distance_height = global.view.ol_driver.get_distance(common.top_left, common.top_right);
                distance = distance_height / data_grid.square.relative_height;
            }

            log.debug(`Grid.compute_distance_for_gis: distance = ${distance}`);
        }

        return distance;
    };

    /**
     * @brief Get the distance of the common grid
     * @returns float common distance
     */
    this.common_distance = () => {
        let common_grid = this.get_common_grid_data_object();
        if (common_grid !== undefined) {
            let data = JSON.parse(common_grid);
            log.debug(`Grid.common_distance: distance = ${data.distance}`);
            return data.distance;
        }
        log.warn("Grid.common_distance: no value");
        return 0.0;
    };

    /**
     * @brief save common grid settings in all desktop storages
     */
    this.apply_to_all_desktops = () => {
        let common_grid = this.get_common_grid_data_object();
        if (common_grid !== undefined) {
            log.debug("Grid.apply_to_all_desktops", {common_grid});
            global.storage.selected_space.update_all_grids(common_grid);
        }
    };

    /**
     * @brief get the configuration of the commisioning files
     * @returns JSON data with commissioning plus few attributes related to the picture size
     */
    this.get_default_configuration = () => {
        // Get the commissioning values
        let json_data = conf.default();
        log.debug("Grid.get_default_configuration", json_data);

        // Add some attributes that will be used to store calculated values
        json_data.resized_grid = {};
        json_data.resized_grid.width = 0;
        json_data.resized_grid.height = 0;
        json_data.grid.common_size = {};
        json_data.grid.common_size.width = 0;
        json_data.grid.common_size.height = 0;
        json_data.grid.size = {};
        json_data.grid.size.width = 0;
        json_data.grid.size.height = 0;

        return json_data;
    };

    /**
     * @brief compute D3 position with rubik top left as (0,0) of most right, left, top, bottom of the screen
     * @returns dictionary  with position of most right, left, top, bottom of the screen
     */
    let compute_absolute_gis_range = function () {
        log.debug("Grid.compute_absolute_gis_range IN :");
        let min_x, max_x, min_y, max_y;
        let origin; // position of the rubik, in pixel

        // get the minimum and maximum coordinates in D3
        let top_left = global.scene.transform_coordinates_3({x: 0, y: 0});
        let bottom_right = global.scene.transform_coordinates_3({
            x: global.view.width,
            y: global.view.height
        });

        // Find the min and max squares

        origin = conf.get("origin.x");

        // find the number of the leftest square of the screen
        min_x = top_left.x - origin;

        // find the number of the rightest square of the screen
        max_x = bottom_right.x - origin;

        origin = conf.get("origin.y");
        //let width = conf.get("grid.common_size.width");

        // find the number of the upper square of the screen
        min_y = top_left.y - origin;

        // find the number of the downest square of the screen
        max_y = bottom_right.y - origin;

        return {min_x, max_x, min_y, max_y};
    };

    /**
     *
     * @param {*} val number to convert
     * @param {*} tail temporary value of the already computed letters
     * @returns the conversion of a number to a base 26 starting at A and finishing at Z
     */
    let number_to_letters = (val, tail = "") => {
        if (val <= 26) {
            return `${String.fromCharCode(val + 64)}${tail}`;
        }

        const remainder = val % 26 || 26;
        const division = Math.trunc(val / 26) - (remainder === 26 ? 1 : 0);

        return number_to_letters(division, `${String.fromCharCode(remainder + 64)}${tail}`);
    };

    /**
     * @brief add and show a sub_grid/sub_axis
     * @param {*} coordinates D3 coordinates {x,y}
     */
    let add_sub_grid = (coordinates) => {
        log.debug("Grid.add_sub_grid IN :");

        let square_coordinates = {x: 0, y: 0};

        // get the id number of a square
        square_coordinates.x = Math.floor(coordinates.x / conf.get("square.width"));
        square_coordinates.y = Math.floor(coordinates.y / conf.get("square.width"));

        // to calcul the literal id number of a square
        let sign;
        let temp_x = square_coordinates.x;
        let temp_y = square_coordinates.y;
        let axis_coords = [];

        // we start at (A,1) at (0,0) and -A,-1 at (-1,-1)
        temp_x >= 0 && (temp_x += 1);
        temp_y >= 0 && (temp_y += 1);

        // we need to convert number to letter
        // negative and positive axis are symetrical so we just fetch the sign of the number and compute the exact letter and add the sign at the end
        temp_x < 0 ? (sign = "-") && (temp_x = Math.abs(temp_x)) : (sign = "");
        axis_coords.push(sign + number_to_letters(temp_x));

        let reference_position = conf.get("frame.reference.position");
        reference_position == DIRECTION_SOUTH_WEST ? axis_coords.push(-temp_y) : axis_coords.push(temp_y);

        // initialize sub_axis, all four directions
        this.sub_axis = new Array(4);
        for (var d = AXIS_NORTH; d <= AXIS_EAST; d++) {
            let t = d % 2; // axis type
            this.sub_axis[d] = new SubAxis(view, svg, conf, this.sub_axis_layer, t, d, square_coordinates, axis_coords);
            this.sub_axis[d].init();
            this.transform.append_child(this.sub_axis[d].transform);
        }
        sub_axis_list["posx" + square_coordinates.x + "posy" + square_coordinates.y] = this.sub_axis;

        // initialize sub_grid
        let sub_grid = new SubGrid(svg, conf, square_coordinates);
        sub_grid.init();
        this.transform.append_child(sub_grid.transform);
        sub_grids_list["posx" + square_coordinates.x + "posy" + square_coordinates.y] = sub_grid;
    };

    /**
     *
     * @param {*} coordinates square coordinates
     * @brief delete from list and d3/html the sub_axis and sub_grid corresponding to the square coordinates
     */
    let delete_sub_grid = (coordinates) => {
        log.debug("Grid.delete_sub_grid IN :");
        delete sub_axis_list["posx" + coordinates.x + "posy" + coordinates.y];
        delete sub_grids_list["posx" + coordinates.x + "posy" + coordinates.y];
        global.d3.select("#sub_grid rect#posx" + coordinates.x + "posy" + coordinates.y).remove();
        global.d3.select("#sub_axis #posx" + coordinates.x + "posy" + coordinates.y).remove();
    };

    /**
     * @brief check if need to show sub_grids/sub_axis then remove/add dynamically
     */
    let compute_sub_grids = function () {
        log.debug("Grid.compute_sub_grids IN :");
        let coordinates = compute_absolute_gis_range();
        let square_width = conf.get("square.width");

        let new_sub_grids = {};

        // check how many squares are in the screen
        if ((coordinates.max_x - coordinates.min_x) / square_width <= nb_squares_max && (coordinates.max_y - coordinates.min_y) / square_width <= nb_squares_max) {
            // to start the loop at the start of a square
            coordinates.min_x >= 0 ? (coordinates.min_x -= (coordinates.min_x % square_width) - 0.0001) : (coordinates.min_x -= square_width + (coordinates.min_x % square_width) - 0.0001);
            coordinates.min_y >= 0 ? (coordinates.min_y -= (coordinates.min_y % square_width) - 0.0001) : (coordinates.min_y -= square_width + (coordinates.min_y % square_width) - 0.0001);
            // building new_sub_grids/sub_axis list for comparing with existing_sub_grids
            // compute all squares coordinates inside the screen
            for (let i = 0; coordinates.min_x + i <= coordinates.max_x; i += square_width) {
                for (let j = 0; coordinates.min_y + j <= coordinates.max_y; j += square_width) {
                    new_sub_grids[Math.floor((coordinates.min_x + i) / square_width) + "&" + Math.floor((coordinates.min_y + j) / square_width)] = {x: coordinates.min_x + i, y: coordinates.min_y + j};
                }
            }

            // create and add sub_grids/sub_axis that are not already existing
            Object.entries(new_sub_grids).forEach(([key, value]) => {
                log.debug(value);
                // verify if existing_sub_grids have this square_coordinates already
                if (!Object.prototype.hasOwnProperty.call(existing_sub_grids, key)) {
                    add_sub_grid(new_sub_grids[key]);
                }
            });

            // delete sub_grids/sub_axis that doesn't exist anymore
            Object.entries(existing_sub_grids).forEach(([key, value]) => {
                log.debug(value);
                if (!Object.prototype.hasOwnProperty.call(new_sub_grids, key)) {
                    coordinates = key.split("&");
                    delete_sub_grid({x: coordinates[0], y: coordinates[1]});
                }
            });
        }
        // if not zoomed enought remove all sub_grids/sub_axis
        else {
            Object.entries(existing_sub_grids).forEach(([key, value]) => {
                log.debug(value);
                coordinates = key.split("&");
                delete_sub_grid({x: coordinates[0], y: coordinates[1]});
            });
        }
        existing_sub_grids = new_sub_grids;
        global.scene.map.grid.frame.display_square_distance();
    };

    // after construction, hide everything
    this.hide();
};
export default Grid;
